<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
} from 'vue';
import draggable from 'vuedraggable';
import { sortBy } from 'lodash';
import InputCheckboxBoolean from '@/components/cifra/InputCheckboxBoolean.vue';
import InputEmojiPicker from '@/components/cifra/InputEmojiPicker.vue';
import { modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  error: { type: [String, Array], default: null },
  tipoNota: { type: String, default: 'numerica' },
});

const emits = defineEmits(['update:modelValue']);

// Estado reactivo
const opciones = ref([...props.modelValue]);
const actualizando = ref(false);
const refsNombreOpcion = ref({});

// Computed
const opcionesNoEliminadas = computed(() => sortBy(opciones.value.filter((o) => !o._destroy), 'orden'));

// Watchers
watch(() => props.modelValue, (nuevasOpciones) => {
  actualizando.value = true;
  opciones.value = [...nuevasOpciones];
  nextTick(() => {
    actualizando.value = false;
  });
});

watch(opciones, (valor) => {
  if (!actualizando.value) {
    emits('update:modelValue', valor);
  }
}, { deep: true });

// Métodos
const cerrarTodosPickers = () => {
  // Ya no es necesario, el componente InputEmojiPicker maneja esto
};

const eliminarOpcion = (opcion) => {
  if (opcion.id) {
    opcion._destroy = true;
  } else {
    const indice = opciones.value.indexOf(opcion);
    opciones.value.splice(indice, 1);
  }
};

const agregarOpcion = () => {
  opciones.value.push({});
  nextTick(() => {
    const ultimoIndice = opcionesNoEliminadas.value.length - 1;
    const ultimoNombreOpcion = refsNombreOpcion.value[`nombreOpcion${ultimoIndice}`];
    if (ultimoNombreOpcion) {
      ultimoNombreOpcion.focus();
    }
  });
};

const actualizarOrden = (lista) => {
  let orden = 1;
  lista.forEach((opcion) => {
    opcion.orden = orden;
    orden += 1;
  });
};

const indiceOpcion = (opcion) => opcionesNoEliminadas.value.indexOf(opcion);

// Watchers
watch(() => props.tipoNota, () => {
  cerrarTodosPickers();
});
</script>

<template>
  <div class="m-b">
    <table class="table m-b-none">
      <thead>
        <tr>
          <th class="col-min"></th>
          <th>
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/opcion.nombre') }}
          </th>
          <th>
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/opcion.valor') }}
          </th>
          <th class="text-center">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('suspenso') }}
          </th>
          <th class="col-min">
            <button
              type="button"
              class="btn btn-cifra btn-xs"
              @click="agregarOpcion"
            >
              <i class="fa fa-plus"></i>
            </button>
          </th>
        </tr>
      </thead>
      <draggable
        :model-value="opcionesNoEliminadas"
        item-key="indiceOpcion"
        tag="tbody"
        handle=".icono-ordenar"
        class="draggable"
        @update:model-value="actualizarOrden($event)"
      >
        <template #item="{ element }">
          <tr>
            <td>
              <i class="icono-ordenar fa fa-bars"></i>
            </td>
            <td>
              <input
                :ref="(el) => { refsNombreOpcion[`nombreOpcion${indiceOpcion(element)}`] = el }"
                v-model="element.nombre"
                class="form-control"
              >
            </td>
            <td>
              <InputEmojiPicker
                v-model="element.valor"
                :placeholder="$I18n.t('seleccion_icono_o_texto')"
              />
            </td>
            <td class="text-center">
              <InputCheckboxBoolean v-model="element.suspenso" />
            </td>
            <td>
              <i
                class="fa fa-trash text-danger clickable"
                @click="eliminarOpcion(element)"
              ></i>
            </td>
          </tr>
        </template>
      </draggable>
    </table>
    <p v-if="error" class="text-danger">
      {{ modelErrorsToHtmlText(error) }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
table > tbody > tr > td:last-child {
  text-align: center;
}

table > tbody > tr > td {
  vertical-align: middle;
}

abbr {
  border: 0;
  text-decoration: none;
  color: #441311;
}

.icono-ordenar {
  cursor: pointer;
}
</style>
